import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { FaSearch, FaGlobe, FaStore, FaWarehouse, FaExchangeAlt, FaClipboardCheck, FaEye, FaFilter, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getLocalBranchNamesForInventory, getInventoryDataForBranch, updateInventoryDataForBranch, bulkDistributeInventory, getMetricsForProducts, createDistributionRequest, createBranchStockRequest, getApprovalCounts, getCategories } from "../services/ClientService"; // Adjust path accordingly
import { toast } from "react-toastify";

const InventoryManagement = () => {
  const navigate = useNavigate();
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchOptions, setBranchOptions] = useState([]);
  const [inventoryData, setInventoryData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [fromExpiryDate, setFromExpiryDate] = useState(null);
  const [toExpiryDate, setToExpiryDate] = useState(null);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [updatingQuantity, setUpdatingQuantity] = useState(false);
  const [editedQuantities, setEditedQuantities] = useState({});
  const [isLoading, setIsLoading] = useState(false); // General loading state for API calls
  const [hoveredProduct, setHoveredProduct] = useState(null); // New state for hovered product

  // User Permissions State - for restricting access for regular branch users
  const [userPermissions, setUserPermissions] = useState({
    isRestrictedUser: false,
    canEditInventory: true,
    canDistribute: true,
    userBranchId: null,
    role: null,
    supportAdminAccess: true,
    masterAccount: false
  });

  // Approval count for badge
  const [approvalCount, setApprovalCount] = useState(0);

  // Bulk Distribution States
  const [showBulkDistribution, setShowBulkDistribution] = useState(false);
  const [targetBranch, setTargetBranch] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectAllProducts, setSelectAllProducts] = useState(false);
  const [selectOnlyCommonInventory, setSelectOnlyCommonInventory] = useState(false);
  const [distributionQuantities, setDistributionQuantities] = useState({});
  const [isDistributing, setIsDistributing] = useState(false);
  const [bulkQuantity, setBulkQuantity] = useState('');
  const [bulkCommonQuantity, setBulkCommonQuantity] = useState('');
  const [targetBranchInventory, setTargetBranchInventory] = useState([]);
  const [loadingTargetInventory, setLoadingTargetInventory] = useState(false);
  const [bulkDistributionSearchQuery, setBulkDistributionSearchQuery] = useState('');
  const [debouncedBulkSearchQuery, setDebouncedBulkSearchQuery] = useState('');

  // Category / subcategory filters
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  // Filter modal state
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  // Pending filters (only applied when clicking Apply Filters)
  const [pendingCategory, setPendingCategory] = useState(null);
  const [pendingSubcategory, setPendingSubcategory] = useState(null);

  // Common Inventory States
  const [metrics, setMetrics] = useState([]); // Available metrics
  const [commonInventoryData, setCommonInventoryData] = useState({}); // { productId: { quantity: '', metricId: '', metricType: '' } }
  const [updatingCommonInventory, setUpdatingCommonInventory] = useState(false);
  
  // Bulk Distribution Common Inventory States
  const [bulkCommonInventoryData, setBulkCommonInventoryData] = useState({}); // { productId: { quantity: '', mainMetric: {}, isCommonInventory: false } }

  // Branch Stock Request States (for branch users requesting stock from warehouse)
  const [showStockRequest, setShowStockRequest] = useState(false);
  const [sourceWarehouse, setSourceWarehouse] = useState(null);
  const [warehouseInventoryData, setWarehouseInventoryData] = useState([]);
  const [loadingWarehouseInventory, setLoadingWarehouseInventory] = useState(false);
  const [stockRequestProducts, setStockRequestProducts] = useState([]);
  const [stockRequestQuantities, setStockRequestQuantities] = useState({});
  const [isRequestingStock, setIsRequestingStock] = useState(false);
  const [stockRequestCommonData, setStockRequestCommonData] = useState({});
  const [stockRequestBulkQty, setStockRequestBulkQty] = useState('');
  const [stockRequestSelectAll, setStockRequestSelectAll] = useState(false);
  const [stockRequestSelectCommon, setStockRequestSelectCommon] = useState(false);
  const [stockRequestBulkCommonQty, setStockRequestBulkCommonQty] = useState('');
  const [stockRequestSearch, setStockRequestSearch] = useState('');
  const [debouncedStockRequestSearch, setDebouncedStockRequestSearch] = useState('');

  const inputRefs = useRef({}); // Ref to store input elements

  const attachWheelListener = (inputElement) => {
    if (inputElement && !inputRefs.current[inputElement]) {
      inputElement.addEventListener(
        "wheel",
        (event) => {
          event.preventDefault(); // Prevent scrolling
        },
        { passive: false }
      );
      inputRefs.current[inputElement] = true; // Mark as attached
    }
  };

  // Debouncing effect for bulk distribution search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedBulkSearchQuery(bulkDistributionSearchQuery);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [bulkDistributionSearchQuery]);

  // Debouncing effect for stock request search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedStockRequestSearch(stockRequestSearch);
    }, 500);
    return () => clearTimeout(handler);
  }, [stockRequestSearch]);

  // Fetch categories for inventory filtering
  useEffect(() => {
    const fetchCategoriesData = async () => {
      try {
        const data = await getCategories({ includeSubcategories: true });
        if (data.success && data.data) {
          const parentCategories = data.data.map((cat) => ({
            value: cat._id,
            label: cat.categoryName,
            subcategories: cat.subcategories || []
          }));
          setCategoryOptions(parentCategories);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategoriesData();
  }, []);

  // Update subcategory options when selected category changes (for initial load)
  useEffect(() => {
    if (selectedCategory) {
      const category = categoryOptions.find((cat) => cat.value === selectedCategory?.value);
      if (category?.subcategories?.length > 0) {
        setSubcategoryOptions(
          category.subcategories.map((sub) => ({
            value: sub._id,
            label: sub.categoryName
          }))
        );
      } else {
        setSubcategoryOptions([]);
      }
    } else {
      setSubcategoryOptions([]);
    }
    setSelectedSubcategory(null);
  }, [selectedCategory, categoryOptions]);

  // Compute modal subcategory options based on pending category
  const modalSubcategoryOptions = useMemo(() => {
    if (!pendingCategory) return [];
    const category = categoryOptions.find((cat) => cat.value === pendingCategory?.value);
    if (category?.subcategories?.length > 0) {
      return category.subcategories.map((sub) => ({
        value: sub._id,
        label: sub.categoryName
      }));
    }
    return [];
  }, [pendingCategory, categoryOptions]);

  // Fetch metrics on component mount
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await getMetricsForProducts({});
        if (response.code === 200 && response.success) {
          const formattedMetrics = [];
          response.data.forEach(metric => {
            // Add primary metric
            formattedMetrics.push({
              value: `${metric._id}_primary`,
              label: metric.metricLabel,
              metricId: metric._id,
              metricType: 'primary',
              metricLabel: metric.metricLabel,
              metricName: metric.metricName
            });
            
            // Add supporting metric if exists
            if (metric.supportingMetricLabel) {
              formattedMetrics.push({
                value: `${metric._id}_supporting`,
                label: metric.supportingMetricLabel,
                metricId: metric._id,
                metricType: 'supporting',
                metricLabel: metric.supportingMetricLabel,
                metricName: metric.supportingMetricName
              });
            }
          });
          setMetrics(formattedMetrics);
        }
      } catch (error) {
        console.error("Error fetching metrics:", error);
      }
    };

    fetchMetrics();
  }, []);

  // Debug log for userPermissions
  useEffect(() => {
    console.log('🔍 UserPermissions Updated:', userPermissions);
  }, [userPermissions]);

  // Fetch approval counts when branch changes
  const fetchApprovalCounts = useCallback(async () => {
    try {
      const response = await getApprovalCounts({});
      if (response.code === 200 && response.success) {
        setApprovalCount(response.data?.total || 0);
      }
    } catch (error) {
      console.error("Error fetching approval counts:", error);
    }
  }, []);

  useEffect(() => {
    const fetchBranchNames = async () => {
      setIsLoading(true);
      setLoadingBranches(true);
      try {
        const response = await getLocalBranchNamesForInventory({});
        if (response.code === 200 && response.success) {
          // Set user permissions from API response
          if (response.userPermissions) {
            setUserPermissions(response.userPermissions);
            console.log('User permissions set:', response.userPermissions);
          }
          
          const branchOptionsFormatted = response.data.map(branch => ({
            value: branch._id,
            label: branch.masterAccount ? `${branch.branchName} (Warehouse)` : branch.branchName,
            ...branch
          }));
          
          // Sort branches: master accounts (warehouses) first, then regular branches
          const sortedBranches = branchOptionsFormatted.sort((a, b) => {
            if (a.masterAccount && !b.masterAccount) return -1;
            if (!a.masterAccount && b.masterAccount) return 1;
            return 0;
          });
          
          setBranchOptions(sortedBranches);
          if (sortedBranches.length > 0) {
            // For restricted users, default to their own branch (not the warehouse)
            if (response.userPermissions?.isRestrictedUser) {
              const ownBranch = sortedBranches.find(b => b.value === response.userPermissions.userBranchId?.toString());
              setSelectedBranch(ownBranch || sortedBranches.find(b => !b.isWarehouse && !b.masterAccount) || sortedBranches[0]);
            } else {
              setSelectedBranch(sortedBranches[0]);
            }
          }
        } else {
          console.error("Failed to fetch branch names:", response);
        }
        
        // Fetch approval counts
        await fetchApprovalCounts();
      } catch (error) {
        console.error("Error fetching branch names:", error);
      } finally {
        setLoadingBranches(false);
        setIsLoading(false);
      }
    };

    fetchBranchNames();
  }, [fetchApprovalCounts]);

  // Modified API call to include searchQuery and filters
  const fetchInventoryForBranch = useCallback(async (branchId) => {
    if (!branchId) return;
    setIsLoading(true);
    try {
      const params = {
        selectedBranchId: branchId,
        searchTerm: searchQuery,
        fromExpiryDate: fromExpiryDate ? fromExpiryDate.toLocaleDateString('en-GB') : null,
        toExpiryDate: toExpiryDate ? toExpiryDate.toLocaleDateString('en-GB') : null,
        categoryId: selectedCategory?.value || null,
        subCategoryId: selectedSubcategory?.value || null,
      };
      const response = await getInventoryDataForBranch(params);
      if (response.code === 200 && response.success) {
        const transformedData = transformDataForTable(response.data || []);
        setInventoryData(transformedData);
        
        // Initialize commonInventoryData for products with commonInventoryType
        const commonInvData = {};
        transformedData.forEach(product => {
          if (product.commonInventoryType) {
            let metricLabel = '';
            let metricId = '';
            let selectedMetric = null;
            let supportingMetricLabel = null;
            let primaryToSupporting = null;
            
            // Try to use mainMetric from product data (preferred)
            if (product.mainMetric && product.mainMetric.metricId) {
              metricLabel = product.mainMetric.metricLabel || '';
              metricId = product.mainMetric.metricId || '';
              selectedMetric = metrics.find(m => 
                m.metricId === product.mainMetric.metricId && m.metricType === 'primary'
              );
              supportingMetricLabel = selectedMetric?.supportingMetricLabel || null;
              primaryToSupporting = selectedMetric?.primaryToSupporting || null;
            } 
            // Fallback to selectedMetric if mainMetric not available
            else if (product.selectedMetric) {
              metricLabel = product.selectedMetric;
              selectedMetric = metrics.find(m => m.label === product.selectedMetric);
              metricId = selectedMetric?.metricId || '';
              supportingMetricLabel = selectedMetric?.supportingMetricLabel || null;
              primaryToSupporting = selectedMetric?.primaryToSupporting || null;
            }
              
            commonInvData[product._id] = {
              quantity: parseFloat((product.availableQuantity || 0).toFixed(3)),
              metricLabel: metricLabel,
              metricId: metricId,
              selectedMetric: selectedMetric || null,
              supportingMetricLabel: supportingMetricLabel,
              primaryToSupporting: primaryToSupporting
            };
          }
        });
        setCommonInventoryData(commonInvData);
      } else {
        console.error("Failed to fetch inventory data:", response);
        setInventoryData([]);
      }
    } catch (error) {
      console.error("Error fetching inventory data:", error);
      setInventoryData([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, selectedCategory, selectedSubcategory, metrics, fromExpiryDate, toExpiryDate]);

  // Branch change handler - useEffect will handle the fetch
  const handleBranchChange = (selectedOption) => {
    console.log('Selected branch:', selectedOption); // Debug log
    console.log('Is warehouse?', selectedOption?.isWarehouse); // Debug log
    console.log('Is master account?', selectedOption?.masterAccount); // Debug log
    setSelectedBranch(selectedOption);
  };

  // Fetch inventory when branch, search query, or filters change
  useEffect(() => {
    if (selectedBranch) {
      // If only searchQuery or filters changed, debounce the API call
      // If branch changed, fetch immediately
      const debounceTimer = setTimeout(() => {
        fetchInventoryForBranch(selectedBranch.value);
      }, 300); // Reduced debounce time for better UX
      return () => clearTimeout(debounceTimer);
    }
  }, [searchQuery, selectedCategory, selectedSubcategory, selectedBranch, fetchInventoryForBranch]);

  function transformDataForTable(branchInventoryData) {
    if (!branchInventoryData) return [];
    return branchInventoryData.map(product => ({
      _id: product._id,
      productName: product.productName,
      commonInventoryType: product.commonInventoryType || false,
      availableQuantity: product.availableQuantity || 0,
      selectedMetric: product.selectedMetric || null,
      mainMetric: product.mainMetric || null, // Include mainMetric from product
      useMultiAttributeMode: product.useMultiAttributeMode || false,
      optionGroups: product.optionGroups || [],
      options: product.inventory.map(option => ({
        _id: option._id,
        weight: option.weight,
        combinationLabel: option.combinationLabel || null,
        selectedOptions: option.selectedOptions || null,
        isMultiAttribute: option.isMultiAttribute || false,
        originalPrice: option.originalPrice,
        discount: option.discount,
        discountPrice: option.discountPrice,
        availableQuantity: option.availableQuantity,
        soldOnline: parseFloat((option.soldOnline || 0).toFixed(3)),
        soldOffline: parseFloat((option.soldOffline || 0).toFixed(3)),
        selectedMetric: option.selectedMetric || null,
        metricId: option.weight?.metricId || null,
        expiryDate: option.expiryDate || null
      }))
    }));
  }

  const getExpiryStyle = (expiryDate) => {
    if (!expiryDate || typeof expiryDate !== 'string') return "text-gray-500";
    const [day, month, year] = expiryDate.split('/').map(Number);
    if (!day || !month || !year) return "text-gray-500";
    const exp = new Date(year, month - 1, day);
    const today = new Date();
    exp.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
    if (diffDays <= 5) return "text-red-600";
    if (diffDays <= 30) return "text-yellow-600";
    return "text-green-600";
  };

  // Helper function to get display label for product option (handles both weight and multi-attribute)
  const getOptionDisplayLabel = (option) => {
    // For multi-attribute products, use combinationLabel
    if (option.combinationLabel) {
      return option.combinationLabel;
    }
    // For regular products, use weight
    if (typeof option.weight === 'object' && option.weight !== null) {
      return option.weight.weightLabel || `${option.weight.weightValue} ${option.weight.metricLabel}`;
    }
    return option.weight || 'N/A';
  };

  const handleQuantityChange = async (productName, optionId, newQuantity, product) => {
    let validQuantity;
    if (newQuantity === "") {
      validQuantity = 0;
    } else {
      const parsedQuantity = parseInt(newQuantity, 10);
      validQuantity = isNaN(parsedQuantity) || parsedQuantity < 0 ? 0 : parsedQuantity;
    }

    setUpdatingQuantity(true);
    setIsLoading(true);

    try {
      const response = await updateInventoryDataForBranch({
        selectedBranchId: selectedBranch.value,
        selectedProductId: product._id,
        selectedProductOptionId: optionId,
        quantity: validQuantity,
      });

      if (response.code === 200 && response.success) {
        console.log(`Updated ${productName} (Option: ${optionId}) to ${validQuantity}`);
        // Instead of calling fetchInventoryForBranch, update local inventory state directly:
        setInventoryData(prevInventory =>
          prevInventory.map(p => {
            if (p._id === product._id) {
              return {
                ...p,
                options: p.options.map(opt =>
                  opt._id === optionId ? { ...opt, availableQuantity: validQuantity } : opt
                )
              };
            }
            return p;
          })
        );
      } else {
        console.error("Failed to update inventory:", response);
      }
    } catch (error) {
      console.error("Error updating inventory:", error);
      // In error case, no re-fetch is done.
    } finally {
      setUpdatingQuantity(false);
      setIsLoading(false);
    }
  };

  // Handler to update local input state for regular inventory (integer only)
  const handleInputChange = (optionId, value) => {
    // Only allow integer values for non-common inventory products
    if (value === '' || /^\d*$/.test(value)) {
      setEditedQuantities(prev => ({ ...prev, [optionId]: value }));
    }
  };

  const handleInputBlur = (productName, optionId, product, eventValue) => {
    const newQuantity = editedQuantities[optionId] ?? eventValue;
    handleQuantityChange(productName, optionId, newQuantity, product);
    setEditedQuantities(prev => {
      const updated = { ...prev };
      delete updated[optionId];
      return updated;
    });
  };

  // Common Inventory Handlers
  const handleCommonInventoryQuantityChange = (productId, value) => {
    // Allow decimal input for common inventory (metric-based)
    // Only allow numbers with up to 1 decimal place
    if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) {
      setCommonInventoryData(prev => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          quantity: value
        }
      }));
    }
  };

  const handleCommonInventoryMetricChange = (productId, selectedMetric) => {
    setCommonInventoryData(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        metricLabel: selectedMetric?.label || '',
        metricId: selectedMetric?.metricId || '',
        selectedMetric: selectedMetric
      }
    }));
  };

  const handleCommonInventoryUpdate = async (product) => {
    const commonData = commonInventoryData[product._id];
    
    if (!commonData?.metricLabel) {
      toast.error('Please select a metric before updating common inventory.');
      return;
    }

    // Use parseFloat for decimal support (e.g., 5.2 Kg)
    const quantity = commonData.quantity === '' ? 0 : parseFloat(commonData.quantity);
    if (isNaN(quantity) || quantity < 0) {
      toast.error('Please enter a valid quantity.');
      return;
    }

    setUpdatingCommonInventory(true);
    setIsLoading(true);

    try {
      // Payload structure for backend
      const payload = {
        selectedBranchId: selectedBranch.value,
        selectedProductId: product._id,
        commonInventory: true,
        quantity: quantity,
        selectedMetric: commonData.metricLabel,
        metricId: commonData.metricId || null  // For future use
      };

      console.log('Updating common inventory with payload:', payload);
      
      const response = await updateInventoryDataForBranch(payload);
      
      if (response.code === 200 && response.success) {
        console.log(`Updated common inventory for ${product.productName}`);
        toast.success('Common inventory updated successfully!');
        // Refresh inventory data
        fetchInventoryForBranch(selectedBranch.value);
      } else {
        console.error("Failed to update common inventory:", response);
        toast.error(response.error || response.message || "Failed to update common inventory.");
      }

    } catch (error) {
      console.error("Error updating common inventory:", error);
      toast.error("An error occurred while updating common inventory.");
    } finally {
      setUpdatingCommonInventory(false);
      setIsLoading(false);
    }
  };

  // Bulk Distribution Handlers
  const handleOpenBulkDistribution = () => {
    // Allow if warehouse OR master account
    if (!selectedBranch?.isWarehouse && !selectedBranch?.masterAccount) {
      toast.error("Bulk distribution can only be initiated from a warehouse branch.");
      return;
    }
    console.log('Opening bulk distribution modal for:', selectedBranch); // Debug log
    setShowBulkDistribution(true);
    setTargetBranch(null);
    setSelectedProducts([]);
    setSelectAllProducts(false);
    setDistributionQuantities({});
  };

  const handleCloseBulkDistribution = () => {
    setShowBulkDistribution(false);
    setTargetBranch(null);
    setSelectedProducts([]);
    setSelectAllProducts(false);
    setDistributionQuantities({});
    setBulkQuantity('');
    setTargetBranchInventory([]);
    setBulkDistributionSearchQuery('');
    setDebouncedBulkSearchQuery('');
    setBulkCommonInventoryData({}); // Reset bulk common inventory data
  };

  // ===== Branch Stock Request Handlers =====
  const handleOpenStockRequest = () => {
    setShowStockRequest(true);
    setSourceWarehouse(null);
    setWarehouseInventoryData([]);
    setStockRequestProducts([]);
    setStockRequestQuantities({});
    setStockRequestCommonData({});
    setStockRequestBulkQty('');
    setStockRequestSelectAll(false);
    setStockRequestSelectCommon(false);
    setStockRequestBulkCommonQty('');
    setStockRequestSearch('');
    setDebouncedStockRequestSearch('');
  };

  const handleCloseStockRequest = () => {
    setShowStockRequest(false);
    setSourceWarehouse(null);
    setWarehouseInventoryData([]);
    setStockRequestProducts([]);
    setStockRequestQuantities({});
    setStockRequestCommonData({});
    setStockRequestBulkQty('');
    setStockRequestSelectAll(false);
    setStockRequestSelectCommon(false);
    setStockRequestBulkCommonQty('');
    setStockRequestSearch('');
    setDebouncedStockRequestSearch('');
  };

  const handleSourceWarehouseChange = async (option) => {
    setSourceWarehouse(option);
    setStockRequestProducts([]);
    setStockRequestQuantities({});
    setStockRequestCommonData({});
    setStockRequestSelectAll(false);
    setStockRequestSelectCommon(false);
    setStockRequestSearch('');
    setDebouncedStockRequestSearch('');
    if (option) {
      setLoadingWarehouseInventory(true);
      try {
        const response = await getInventoryDataForBranch({ selectedBranchId: option.value });
        if (response.code === 200 && response.success) {
          const transformedData = transformDataForTable(response.data || []);
          setWarehouseInventoryData(transformedData);
        } else {
          setWarehouseInventoryData([]);
        }
      } catch (error) {
        console.error("Error fetching warehouse inventory:", error);
        setWarehouseInventoryData([]);
      } finally {
        setLoadingWarehouseInventory(false);
      }
    } else {
      setWarehouseInventoryData([]);
    }
  };

  const handleStockRequestProductSelection = (productId, optionId, checked) => {
    const key = `${productId}_${optionId}`;
    if (checked) {
      setStockRequestProducts(prev => [...prev, key]);
      setStockRequestQuantities(prev => ({ ...prev, [key]: '' }));
    } else {
      setStockRequestProducts(prev => {
        const updated = prev.filter(id => id !== key);
        if (updated.length === 0) setStockRequestSelectAll(false);
        return updated;
      });
      setStockRequestQuantities(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    }
  };

  const handleStockRequestQuantityChange = (productId, optionId, value) => {
    const key = `${productId}_${optionId}`;
    if (value === '') {
      setStockRequestQuantities(prev => ({ ...prev, [key]: '' }));
    } else {
      const parsedValue = parseInt(value, 10);
      if (!isNaN(parsedValue) && parsedValue >= 0) {
        setStockRequestQuantities(prev => ({ ...prev, [key]: parsedValue }));
      }
    }
  };

  const handleStockRequestSelectAll = (checked) => {
    setStockRequestSelectAll(checked);
    if (checked) {
      const allIds = [];
      warehouseInventoryData.forEach(product => {
        product.options.forEach(option => {
          allIds.push(`${product._id}_${option._id}`);
        });
      });
      setStockRequestProducts(allIds);
      const initialQuantities = {};
      allIds.forEach(id => { initialQuantities[id] = ''; });
      setStockRequestQuantities(initialQuantities);
    } else {
      setStockRequestProducts([]);
      setStockRequestQuantities({});
      setStockRequestBulkQty('');
    }
  };

  const handleStockRequestBulkQtyChange = (value) => {
    setStockRequestBulkQty(value);
    if (value === '') {
      const clearedQty = {};
      stockRequestProducts.forEach(key => { clearedQty[key] = ''; });
      setStockRequestQuantities(clearedQty);
    } else {
      const parsedValue = parseInt(value, 10);
      if (!isNaN(parsedValue) && parsedValue >= 0) {
        const updatedQty = { ...stockRequestQuantities };
        stockRequestProducts.forEach(key => { updatedQty[key] = parsedValue; });
        setStockRequestQuantities(updatedQty);
      }
    }
  };

  const handleStockRequestSelectCommon = (checked) => {
    setStockRequestSelectCommon(checked);
    if (checked) {
      const commonProducts = warehouseInventoryData.filter(p => p.commonInventoryType === true);
      const newCommonData = {};
      commonProducts.forEach(product => {
        newCommonData[product._id] = {
          productId: product._id,
          isCommonInventory: true,
          quantity: stockRequestBulkCommonQty || '',
          mainMetric: product.mainMetric || 'Kg'
        };
      });
      setStockRequestCommonData(newCommonData);
      setStockRequestProducts([]);
      setStockRequestQuantities({});
      setStockRequestSelectAll(false);
    } else {
      setStockRequestCommonData({});
      setStockRequestBulkCommonQty('');
    }
  };

  const handleStockRequestBulkCommonQtyChange = (value) => {
    setStockRequestBulkCommonQty(value);
    setStockRequestCommonData(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(pid => {
        if (updated[pid]?.isCommonInventory) {
          updated[pid] = { ...updated[pid], quantity: value };
        }
      });
      return updated;
    });
  };

  const handleStockRequestCommonCheckbox = (productId, checked, product) => {
    setStockRequestCommonData(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        isCommonInventory: checked,
        quantity: '',
        mainMetric: product?.mainMetric || null
      }
    }));
  };

  const handleStockRequestCommonQtyChange = (productId, value) => {
    if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) {
      setStockRequestCommonData(prev => ({
        ...prev,
        [productId]: { ...prev[productId], quantity: value }
      }));
    }
  };

  const handleSubmitStockRequest = async () => {
    if (!sourceWarehouse) {
      toast.error("Please select a source warehouse.");
      return;
    }

    const hasCommonSelected = Object.values(stockRequestCommonData).some(d => d?.isCommonInventory);
    if (stockRequestProducts.length === 0 && !hasCommonSelected) {
      toast.error("Please select at least one product to request.");
      return;
    }

    let hasInvalidQuantity = false;
    const inventoryUpdates = [];
    const exceedingProducts = [];

    // Process regular product selections
    stockRequestProducts.forEach(key => {
      const [productId, optionId] = key.split('_');
      const product = warehouseInventoryData.find(p => p._id === productId);
      if (product?.commonInventoryType) return;

      const quantity = stockRequestQuantities[key] === '' ? 0 : (stockRequestQuantities[key] || 0);
      if (quantity <= 0) { hasInvalidQuantity = true; return; }

      const option = product?.options.find(o => o._id === optionId);
      if (!option || option.availableQuantity < quantity) {
        const weightDisplay = getOptionDisplayLabel(option || {});
        exceedingProducts.push(`${product?.productName} (${weightDisplay}): Requested ${quantity}, Available ${option?.availableQuantity || 0}`);
        hasInvalidQuantity = true;
        return;
      }

      inventoryUpdates.push({
        productId, productOptionId: optionId, quantity, commonInventory: false
      });
    });

    // Process common inventory selections
    warehouseInventoryData.forEach(product => {
      if (product?.commonInventoryType) {
        const commonData = stockRequestCommonData[product._id];
        if (commonData?.isCommonInventory) {
          if (!commonData?.mainMetric && !product?.mainMetric) {
            toast.error(`Main metric not found for ${product.productName}`);
            hasInvalidQuantity = true;
            return;
          }
          const quantity = commonData.quantity === '' ? 0 : parseFloat(commonData.quantity);
          if (isNaN(quantity) || quantity <= 0) {
            toast.error(`Please enter a valid quantity for ${product.productName}`);
            hasInvalidQuantity = true;
            return;
          }
          const availableQuantities = product?.options.map(opt => opt.availableQuantity) || [];
          const minAvailable = Math.min(...availableQuantities);
          if (!product?.options || product.options.length === 0 || minAvailable < quantity) {
            exceedingProducts.push(`${product?.productName} (Common): Requested ${quantity}, Available: ${minAvailable || 0}`);
            hasInvalidQuantity = true;
            return;
          }
          const mainMetricToUse = commonData?.mainMetric || product?.mainMetric;
          product.options.forEach(option => {
            inventoryUpdates.push({
              productId: product._id, productOptionId: option._id, quantity,
              commonInventory: true, selectedMetric: mainMetricToUse?.metricLabel, metricId: mainMetricToUse?.metricId
            });
          });
        }
      }
    });

    if (hasInvalidQuantity) {
      if (exceedingProducts.length > 0) {
        toast.error(`Products exceed available warehouse quantity: ${exceedingProducts.join(', ')}. Please adjust the quantities and try again.`);
      } else {
        toast.error("Please enter valid quantities greater than 0 for all selected products.");
      }
      return;
    }

    setIsRequestingStock(true);
    setIsLoading(true);

    try {
      const items = inventoryUpdates.map(update => {
        const product = warehouseInventoryData.find(p => p._id === update.productId);
        const option = product?.options.find(o => o._id === update.productOptionId);
        const weightDisplay = getOptionDisplayLabel(option || {});
        return {
          productId: update.productId,
          productOptionId: update.productOptionId,
          productName: product?.productName || 'Unknown Product',
          optionLabel: weightDisplay,
          weight: weightDisplay,
          quantity: update.quantity,
          commonInventory: update.commonInventory,
          selectedMetric: update.selectedMetric,
          metricId: update.metricId
        };
      });

      const payload = {
        warehouseBranchId: sourceWarehouse.value,
        items,
        notes: ''
      };

      const response = await createBranchStockRequest(payload);

      if (response.code === 200 && response.success) {
        toast.success(`Stock request created successfully! Request ID: ${response.data.requestId} — ${response.data.itemCount} item(s) sent to ${response.data.warehouseName} for review.`);
        handleCloseStockRequest();
        fetchInventoryForBranch(selectedBranch.value);
        fetchApprovalCounts();
      } else {
        toast.error(response.error || response.message || "Failed to create stock request.");
      }
    } catch (error) {
      console.error("Error creating stock request:", error);
      const errorMessage = error.message || error.errorData?.error || error.errorData?.message || "An error occurred while creating stock request.";
      toast.error(errorMessage);
    } finally {
      setIsRequestingStock(false);
      setIsLoading(false);
    }
  };

  const handleTargetBranchChange = (branch) => {
    setTargetBranch(branch);
    if (!branch) {
      setTargetBranchInventory([]);
    }
    // Reset search when changing target branch
    setBulkDistributionSearchQuery('');
    setDebouncedBulkSearchQuery('');
  };

  // Fetch target branch inventory when target branch or search query changes
  useEffect(() => {
    const fetchTargetInventory = async () => {
      if (targetBranch) {
        setLoadingTargetInventory(true);
        try {
          const response = await getInventoryDataForBranch({
            selectedBranchId: targetBranch.value,
            searchTerm: debouncedBulkSearchQuery,
          });
          if (response.code === 200 && response.success) {
            const transformedData = transformDataForTable(response.data || []);
            setTargetBranchInventory(transformedData);
          }
        } catch (error) {
          console.error('Error fetching target branch inventory:', error);
        } finally {
          setLoadingTargetInventory(false);
        }
      }
    };

    if (showBulkDistribution && targetBranch) {
      fetchTargetInventory();
    }
  }, [debouncedBulkSearchQuery, targetBranch, showBulkDistribution]);

  const handleSelectAllProducts = (checked) => {
    setSelectAllProducts(checked);
    if (checked) {
      const allProductOptionIds = [];
      inventoryData.forEach(product => {
        product.options.forEach(option => {
          allProductOptionIds.push(`${product._id}_${option._id}`);
        });
      });
      setSelectedProducts(allProductOptionIds);
      // Initialize distribution quantities to empty string
      const initialQuantities = {};
      allProductOptionIds.forEach(id => {
        initialQuantities[id] = '';
      });
      setDistributionQuantities(initialQuantities);
    } else {
      setSelectedProducts([]);
      setDistributionQuantities({});
      setBulkQuantity(''); // Clear bulk quantity input
    }
  };

  const handleProductSelection = (productId, optionId, checked) => {
    const key = `${productId}_${optionId}`;
    if (checked) {
      setSelectedProducts(prev => [...prev, key]);
      setDistributionQuantities(prev => ({ ...prev, [key]: '' }));
    } else {
      setSelectedProducts(prev => {
        const updated = prev.filter(id => id !== key);
        // If no products selected, uncheck Select All
        if (updated.length === 0) {
          setSelectAllProducts(false);
        }
        return updated;
      });
      setDistributionQuantities(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    }
  };

  // Handle selecting only common inventory products
  const handleSelectOnlyCommonInventory = (checked) => {
    setSelectOnlyCommonInventory(checked);
    
    if (checked) {
      // Filter for common inventory products only
      const commonInventoryProducts = inventoryData.filter(
        product => product.commonInventoryType === true
      );
      
      // Use bulk common inventory data
      const newBulkData = {};
      commonInventoryProducts.forEach(product => {
        newBulkData[product._id] = {
          productId: product._id,
          isCommonInventory: true,
          quantity: bulkCommonQuantity || '',
          totalWeight: product.totalWeight || 0,
          mainMetric: product.mainMetric || 'Kg'
        };
      });
      setBulkCommonInventoryData(newBulkData);
      
      // Clear regular product selections
      setSelectedProducts([]);
      setDistributionQuantities({});
      setSelectAllProducts(false);
    } else {
      // Clear common inventory selections
      setBulkCommonInventoryData({});
      setBulkCommonQuantity('');
    }
  };

  // Handle bulk common quantity change
  const handleBulkCommonQuantityChange = (value) => {
    setBulkCommonQuantity(value);
    
    // Apply to all selected common inventory products
    setBulkCommonInventoryData(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(productId => {
        // Only update if the checkbox is checked (isCommonInventory is true)
        if (updated[productId]?.isCommonInventory) {
          updated[productId] = {
            ...updated[productId],
            quantity: value
          };
        }
      });
      return updated;
    });
  };

  // Bulk Common Inventory Handlers
  const handleBulkCommonInventoryCheckboxChange = (productId, checked, product) => {
    setBulkCommonInventoryData(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        isCommonInventory: checked,
        quantity: '',
        mainMetric: product?.mainMetric || null
      }
    }));
  };



  const handleBulkCommonInventoryQuantityChange = (productId, value) => {
    // Allow decimal input for common inventory (metric-based)
    // Only allow numbers with up to 1 decimal place
    if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) {
      setBulkCommonInventoryData(prev => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          quantity: value
        }
      }));
    }
  };

  const handleDistributionQuantityChange = (productId, optionId, value) => {
    const key = `${productId}_${optionId}`;
    // Allow empty string like inventory card inputs
    if (value === '') {
      setDistributionQuantities(prev => ({ ...prev, [key]: '' }));
    } else {
      const parsedValue = parseInt(value, 10);
      if (!isNaN(parsedValue) && parsedValue >= 0) {
        setDistributionQuantities(prev => ({ ...prev, [key]: parsedValue }));
      }
    }
  };

  const handleBulkQuantityChange = (value) => {
    setBulkQuantity(value);
    if (value === '') {
      // Clear all quantities
      const clearedQuantities = {};
      selectedProducts.forEach(key => {
        clearedQuantities[key] = '';
      });
      setDistributionQuantities(clearedQuantities);
    } else {
      const parsedValue = parseInt(value, 10);
      if (!isNaN(parsedValue) && parsedValue >= 0) {
        // Apply to all selected products without validation here
        const updatedQuantities = { ...distributionQuantities };
        selectedProducts.forEach(key => {
          updatedQuantities[key] = parsedValue;
        });
        setDistributionQuantities(updatedQuantities);
      }
    }
  };

  const handleSubmitBulkDistribution = async () => {
    if (!targetBranch) {
      toast.error("Please select a target branch.");
      return;
    }

    // Check if there are any products selected (either regular or common inventory)
    const hasCommonInventorySelected = Object.values(bulkCommonInventoryData).some(data => data?.isCommonInventory);
    
    if (selectedProducts.length === 0 && !hasCommonInventorySelected) {
      toast.error("Please select at least one product to distribute.");
      return;
    }

    // Validate quantities
    let hasInvalidQuantity = false;
    const inventoryUpdates = [];
    const exceedingProducts = [];

    // First, process regular product selections
    selectedProducts.forEach(key => {
      const [productId, optionId] = key.split('_');
      const product = inventoryData.find(p => p._id === productId);
      
      // Skip if this is a common inventory product (handle separately)
      if (product?.commonInventoryType) {
        return;
      }
      
      // Regular inventory update
      const quantity = distributionQuantities[key] === '' ? 0 : (distributionQuantities[key] || 0);
      
      if (quantity <= 0) {
        hasInvalidQuantity = true;
        return;
      }

      // Find warehouse inventory
      const option = product?.options.find(o => o._id === optionId);
      
      if (!option || option.availableQuantity < quantity) {
        const weightDisplay = getOptionDisplayLabel(option || {});
        exceedingProducts.push(`${product?.productName} (${weightDisplay}): Requested ${quantity}, Available ${option?.availableQuantity || 0}`);
        hasInvalidQuantity = true;
        return;
      }

      inventoryUpdates.push({
        productId,
        productOptionId: optionId,
        quantity,
        commonInventory: false
      });
    });

    // Second, process common inventory selections
    inventoryData.forEach(product => {
      if (product?.commonInventoryType) {
        const commonData = bulkCommonInventoryData[product._id];
        
        if (commonData?.isCommonInventory) {
          if (!commonData?.mainMetric && !product?.mainMetric) {
            toast.error(`Main metric not found for ${product.productName}`);
            hasInvalidQuantity = true;
            return;
          }
          
          // Use parseFloat for decimal support (e.g., 5.2 Kg)
          const quantity = commonData.quantity === '' ? 0 : parseFloat(commonData.quantity);
          if (isNaN(quantity) || quantity <= 0) {
            toast.error(`Please enter a valid quantity for ${product.productName}`);
            hasInvalidQuantity = true;
            return;
          }
          
          // For common inventory, check warehouse availability and add entries for ALL product options
          const availableQuantities = product?.options.map(opt => opt.availableQuantity) || [];
          const minAvailable = Math.min(...availableQuantities);
          
          if (!product?.options || product.options.length === 0 || minAvailable < quantity) {
            exceedingProducts.push(`${product?.productName} (Common): Requested ${quantity}, Available in all options: ${minAvailable || 0}`);
            hasInvalidQuantity = true;
            return;
          }
          
          // Add an entry for EACH product option with the same quantity and metric
          const mainMetricToUse = commonData?.mainMetric || product?.mainMetric;
          product.options.forEach(option => {
            inventoryUpdates.push({
              productId: product._id,
              productOptionId: option._id,
              quantity,
              commonInventory: true,
              selectedMetric: mainMetricToUse?.metricLabel,
              metricId: mainMetricToUse?.metricId
            });
          });
        }
      }
    });

    if (hasInvalidQuantity) {
      if (exceedingProducts.length > 0) {
        toast.error(`Products exceed available quantity: ${exceedingProducts.join(', ')}. Please adjust the quantities and try again.`);
      } else {
        toast.error("Please enter valid quantities greater than 0 for all selected products.");
      }
      return;
    }

    setIsDistributing(true);
    setIsLoading(true);

    try {
      // Build items array with product details for approval workflow
      const items = [];
      
      // Process regular product updates
      for (const update of inventoryUpdates) {
        const product = inventoryData.find(p => p._id === update.productId);
        const option = product?.options.find(o => o._id === update.productOptionId);
        const weightDisplay = getOptionDisplayLabel(option || {});
        
        items.push({
          productId: update.productId,
          productOptionId: update.productOptionId,
          productName: product?.productName || 'Unknown Product',
          optionLabel: weightDisplay,
          weight: weightDisplay,
          quantity: update.quantity,
          commonInventory: update.commonInventory,
          selectedMetric: update.selectedMetric,
          metricId: update.metricId
        });
      }
      
      const payload = {
        targetBranchId: targetBranch.value,
        items,
        notes: '' // Can add notes field if needed
      };
      
      console.log('Creating distribution request with payload:', payload);
      
      // Use approval workflow instead of direct distribution
      const response = await createDistributionRequest(payload);

      if (response.code === 200 && response.success) {
        toast.success(`Distribution request created successfully! Request ID: ${response.data.requestId} — ${response.data.itemCount} item(s) sent to ${response.data.targetBranchName} for review.`);
        handleCloseBulkDistribution();
        // Refresh warehouse inventory
        fetchInventoryForBranch(selectedBranch.value);
        // Refresh approval counts
        fetchApprovalCounts();
      } else {
        toast.error(response.error || response.message || "Failed to create distribution request.");
      }
    } catch (error) {
      console.error("Error creating distribution request:", error);
      const errorMessage = error.message || error.errorData?.error || error.errorData?.message || "An error occurred while creating distribution request.";
      toast.error(errorMessage);
    } finally {
      setIsDistributing(false);
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-white scrollbar-thin relative">
      {isLoading && (
        <div className="fixed inset-0 flex items-center justify-center bg-white/80 z-50">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}

      <div className="bg-white p-6 sticky top-0 z-10 space-y-4">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center pb-4 max-md:px-4 gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">Inventory</h1>
            {/* Read-only indicator for restricted users */}
            {userPermissions.isRestrictedUser && (
              <span className="flex items-center gap-1.5 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                <FaEye className="text-xs" />
                View Only
              </span>
            )}
          </div>
          {/* Branch Selector with React‑Select */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
            {/* Inventory Approvals Button - Show for all branches */}
            <button
              onClick={() => navigate('/inventory-approvals')}
              className="relative bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-5 py-2.5 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2.5 whitespace-nowrap order-3 sm:order-1"
              title="View and manage inventory distribution approvals"
            >
              <FaClipboardCheck className="text-lg" />
              <span className="hidden sm:inline">Approvals</span>
              {approvalCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {approvalCount > 9 ? '9+' : approvalCount}
                </span>
              )}
            </button>
            {/* Show Distribute button if warehouse OR master account AND user can distribute */}
            {!userPermissions.isRestrictedUser && (selectedBranch?.isWarehouse === true || selectedBranch?.masterAccount === true) && (
              <button
                onClick={handleOpenBulkDistribution}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-5 py-2.5 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2.5 whitespace-nowrap order-2 sm:order-2"
                title="Distribute inventory from warehouse to branches"
              >
                <FaWarehouse className="text-lg" />
                <FaExchangeAlt className="text-sm" />
                <span className="hidden sm:inline">Distribute to Branches</span>
                <span className="sm:hidden">Distribute</span>
              </button>
            )}
            {/* Show Request Stock button for role 2 branch users (non-warehouse, non-master) */}
            {userPermissions.role === 2 && !userPermissions.masterAccount && selectedBranch && !selectedBranch?.isWarehouse && !selectedBranch?.masterAccount && (
              <button
                onClick={handleOpenStockRequest}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-5 py-2.5 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2.5 whitespace-nowrap order-2 sm:order-2"
                title="Request stock from warehouse"
              >
                <FaStore className="text-lg" />
                <FaExchangeAlt className="text-sm" />
                <span className="hidden sm:inline">Request Stock from Warehouse</span>
                <span className="sm:hidden">Request Stock</span>
              </button>
            )}
            <div className="w-full sm:max-w-xs lg:max-w-md min-w-[200px] order-1 sm:order-3">
              <Select
                options={userPermissions.isRestrictedUser ? branchOptions.filter(b => !b.isWarehouse && !b.masterAccount) : branchOptions}
                value={selectedBranch}
                placeholder="Select Branch"
                onChange={handleBranchChange}
                className="react-select-container"
                classNamePrefix="react-select"
                isLoading={loadingBranches}
                isDisabled={userPermissions.isRestrictedUser} // Disable branch selector for restricted users
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3">
            {/* Search Bar */}
            <div className="flex-grow relative min-w-0">
              <input
                type="text"
                placeholder="Search Products"
                className="border border-gray-300 rounded-full py-2 pl-11 pr-4 w-full text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm hover:shadow-md transition-shadow duration-300"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="absolute top-1/2 left-4 transform -translate-y-1/2 text-gray-400">
                <FaSearch className="text-lg" />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Filters Button */}
              <button
                onClick={() => {
                  setPendingCategory(selectedCategory);
                  setPendingSubcategory(selectedSubcategory);
                  setShowFiltersModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold shadow-md hover:shadow-lg transition-all duration-300"
              >
                <FaFilter className="text-sm" />
                <span>Filters</span>
                {(selectedCategory || selectedSubcategory) && (
                  <span className="bg-white text-blue-600 text-xs font-bold px-2 py-0.5 rounded-full">
                    {(selectedCategory ? 1 : 0) + (selectedSubcategory ? 1 : 0)}
                  </span>
                )}
              </button>
              <DatePicker
                selected={fromExpiryDate}
                onChange={setFromExpiryDate}
                dateFormat="dd/MM/yyyy"
                placeholderText="From Expiry"
                className="border border-gray-300 rounded-full py-2 px-3 text-sm"
              />
              <DatePicker
                selected={toExpiryDate}
                onChange={setToExpiryDate}
                dateFormat="dd/MM/yyyy"
                minDate={fromExpiryDate}
                placeholderText="To Expiry"
                className="border border-gray-300 rounded-full py-2 px-3 text-sm"
              />
              <button
                onClick={() => {
                  setFromExpiryDate(null);
                  setToExpiryDate(null);
                  setSelectedCategory(null);
                  setSelectedSubcategory(null);
                  setSearchQuery('');
                }}
                className="px-3 py-2 rounded-full bg-gray-700 text-white text-sm font-semibold whitespace-nowrap"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Inventory Items */}
      <div className="px-6 pb-6 pt-1 overflow-y-auto max-h-[calc(100vh-158px)] scrollbar-thin">
        <h3 className="text-xl font-bold mb-4">Inventory Items</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {inventoryData.map((product) => (
            <div
              key={product._id}
              className={`bg-white p-6 rounded-2xl shadow-md border-2 border-gray-200 transition-all duration-300 ${
                userPermissions.isRestrictedUser 
                  ? 'grayscale opacity-75 cursor-not-allowed pointer-events-none' 
                  : 'hover:shadow-xl transform hover:scale-[1.01] hover:-translate-y-0.5 ring-0 hover:ring-2 hover:ring-blue-200/50 hover:border-blue-200'
              } ${hoveredProduct === product.productName && !userPermissions.isRestrictedUser ? 'shadow-lg bg-gradient-to-br from-blue-50/30 to-indigo-50/20 border-blue-300' : ''}`}
              onMouseEnter={() => !userPermissions.isRestrictedUser && setHoveredProduct(product.productName)}
              onMouseLeave={() => !userPermissions.isRestrictedUser && setHoveredProduct(null)}
            >
              <h4 className="font-bold text-gray-800 text-lg mb-4 line-clamp-2">{product.productName}</h4>
              
              {/* Product Options - Disabled if Common Inventory is enabled */}
              {product.options.map((option) => (
                <div key={option._id} className={`border-t border-gray-200 mt-4 pt-4 ${product.commonInventoryType ? 'opacity-40 pointer-events-none grayscale' : ''}`}>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span
                        className={`${option.combinationLabel ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'} text-sm font-semibold px-3 py-1 rounded-full shadow-sm`}
                      >
                        {getOptionDisplayLabel(option)}
                      </span>
                      <div className="flex gap-2">
                        <div className="flex items-center bg-gradient-to-r from-green-50 to-green-100 text-green-700 px-2 py-1 rounded-lg shadow-sm border border-green-200">
                          <FaGlobe className="text-xs mr-1" />
                          <span className="text-xs font-medium">{parseFloat(option.soldOnline).toString()}</span>
                        </div>
                        <div className="flex items-center bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700 px-2 py-1 rounded-lg shadow-sm border border-purple-200">
                          <FaStore className="text-xs mr-1" />
                          <span className="text-xs font-medium">{parseFloat(option.soldOffline).toString()}</span>
                        </div>
                      </div>
                    </div>
                  <div className="flex items-center justify-between">
                    <p
                      className={`text-sm font-bold ${option.availableQuantity === 0 || option.availableQuantity === ""
                          ? "text-red-600"
                          : option.availableQuantity < 10
                            ? "text-yellow-600"
                            : "text-green-600"
                        }`}
                    >
                      {option.availableQuantity === 0 || option.availableQuantity === ""
                        ? "Out of stock"
                        : option.availableQuantity < 10
                          ? "Low Stock"
                          : "In Stock"}
                    </p>
                    {/* Show quantity as display text for restricted users, editable input for others */}
                    {userPermissions.isRestrictedUser ? (
                      <span className={`w-20 text-center border rounded-md bg-gray-100 px-2 py-1 shadow-sm font-medium ${
                        option.availableQuantity === 0 || option.availableQuantity === ""
                          ? "border-red-500 border-2 text-red-600"
                          : option.availableQuantity < 10
                          ? "border-yellow-500 border-2 text-yellow-600"
                          : "border-green-500 border-2 text-green-600"
                      } ${product.commonInventoryType ? 'opacity-0' : ''}`}>
                        {option.availableQuantity || '0'}
                      </span>
                    ) : (
                      <input
                        type="number"
                        ref={(inputElement) => attachWheelListener(inputElement)} // Attach wheel listener
                        className={`w-20 text-center border border-gray-300 rounded-md bg-gray-50 px-2 py-1 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition duration-200 ${
                          option.availableQuantity === 0 || option.availableQuantity === ""
                            ? "border-red-500 border-2"
                            : option.availableQuantity < 10
                            ? "border-yellow-500 border-2"
                            : "border-green-500 border-2"
                        } ${product.commonInventoryType ? 'opacity-0' : ''}`}
                        value={
                          editedQuantities[option._id] !== undefined
                            ? editedQuantities[option._id]
                            : option.availableQuantity === 0
                              ? ''
                              : option.availableQuantity
                        }
                        placeholder="0"
                        onChange={(e) => handleInputChange(option._id, e.target.value)}
                        onBlur={(e) =>
                          handleInputBlur(product.productName, option._id, product, e.target.value)
                        }
                        min="0"
                        step="1"
                        disabled={updatingQuantity || product.commonInventoryType}
                      />
                    )}
                  </div>
                  {option.expiryDate && (
                    <p className={`text-xs font-semibold mt-2 ${getExpiryStyle(option.expiryDate)}`}>
                      Expiry: {option.expiryDate}
                    </p>
                  )}
                </div>
              ))}

              {/* Common Inventory Section - Show when commonInventoryType is true */}
              {product.commonInventoryType && (
                <div className="border-t-4 border-indigo-500 mt-6 pt-4 bg-gradient-to-br from-indigo-50/50 to-purple-50/30 rounded-lg p-4 -mx-2">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="bg-indigo-600 text-white p-1.5 rounded-full">
                      <FaWarehouse className="text-sm" />
                    </div>
                    <h5 className="font-bold text-indigo-900 text-base">Common Inventory (Metric-based)</h5>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <p className="text-xs text-blue-800 leading-relaxed">
                      <strong>📦 Shared Inventory:</strong> This will share inventory across all product options. 
                      For example, if you have <strong>150 {product?.mainMetric?.metricLabel || 'Kg/L/any other metric'}</strong> in stock, it will automatically be available for{' '}
                      <span className="font-semibold">
                        {product.options.map(opt => 
                          typeof opt.weight === 'object' && opt.weight !== null
                            ? opt.weight.weightLabel || `${opt.weight.weightValue} ${opt.weight.metricLabel}`
                            : opt.weight
                        ).join(', ')}
                      </span> options.
                    </p>
                  </div>

                  <div className="space-y-3">
                    {/* Quantity Input with Metric Label */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Total Quantity ({commonInventoryData[product._id]?.metricLabel || 'N/A'}) {!userPermissions.isRestrictedUser && <span className="text-red-500">*</span>}
                      </label>
                      <div className="flex items-center gap-2">
                        {userPermissions.isRestrictedUser ? (
                          // Read-only display for restricted users
                          <span className="flex-1 text-center border-2 border-indigo-300 rounded-md bg-indigo-50 px-3 py-2 shadow-sm text-sm font-bold text-indigo-800">
                            {commonInventoryData[product._id]?.quantity || '0'}
                          </span>
                        ) : (
                          <input
                            type="number"
                            ref={(inputElement) => attachWheelListener(inputElement)}
                            className="flex-1 text-center border-2 border-indigo-300 rounded-md bg-white px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition duration-200 text-sm font-medium"
                            value={commonInventoryData[product._id]?.quantity || ''}
                            onChange={(e) => handleCommonInventoryQuantityChange(product._id, e.target.value)}
                            placeholder={`Enter quantity in ${commonInventoryData[product._id]?.metricLabel || 'metric'}`}
                            min="0"
                            step="0.1"
                            disabled={updatingCommonInventory}
                          />
                        )}
                        {commonInventoryData[product._id]?.metricLabel && (
                          <span className="text-sm font-semibold text-indigo-700 bg-indigo-100 px-3 py-2 rounded-md border border-indigo-200">
                            {commonInventoryData[product._id].metricLabel}
                          </span>
                        )}
                      </div>
                      
                      {/* Note about inventory metric - only show for editable users */}
                      {!userPermissions.isRestrictedUser && (
                        <p className="text-xs text-gray-600 mt-1.5 italic">
                          Inventory must be entered in <strong>{commonInventoryData[product._id]?.metricLabel || 'primary metric'}</strong>
                        </p>
                      )}
                      
                      {/* Conversion Info */}
                      {/* {commonInventoryData[product._id]?.quantity && 
                       commonInventoryData[product._id]?.supportingMetricLabel && 
                       commonInventoryData[product._id]?.primaryToSupporting && 
                       parseFloat(commonInventoryData[product._id]?.quantity) > 0 && (
                        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-green-700 font-semibold text-xs flex items-center gap-1">
                            <span className="text-green-600">✓</span>
                            Conversion: {commonInventoryData[product._id].quantity} {commonInventoryData[product._id].metricLabel} = {' '}
                            {parseFloat(commonInventoryData[product._id].quantity) * commonInventoryData[product._id].primaryToSupporting} {' '}
                            {commonInventoryData[product._id].supportingMetricLabel}
                          </p>
                        </div>
                      )} */}
                    </div>

                    {/* Update Button - Hide for restricted users */}
                    {!userPermissions.isRestrictedUser && (
                      <button
                        onClick={() => handleCommonInventoryUpdate(product)}
                        disabled={updatingCommonInventory || !commonInventoryData[product._id]?.metricLabel}
                        className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-4 py-2.5 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                      >
                        {updatingCommonInventory ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                            <span>Updating...</span>
                          </>
                        ) : (
                          <>
                            <FaWarehouse />
                            <span>Update Common Inventory</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Bulk Distribution Modal */}
      {showBulkDistribution && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <FaWarehouse className="text-3xl" />
                  <div>
                    <h2 className="text-2xl font-bold">Bulk Inventory Distribution</h2>
                    <p className="text-purple-100 text-sm mt-1">Distribute inventory from warehouse to branches</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseBulkDistribution}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Target Branch Selection */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Target Branch *
                </label>
                <Select
                  options={branchOptions.filter(b => !b.isWarehouse && b.value !== selectedBranch?.value)}
                  value={targetBranch}
                  onChange={handleTargetBranchChange}
                  placeholder="Choose branch to distribute inventory..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>

              {/* Search Input */}
              {targetBranch && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Search Products
                  </label>
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={bulkDistributionSearchQuery}
                      onChange={(e) => setBulkDistributionSearchQuery(e.target.value)}
                      placeholder="Search by product name..."
                      className="w-full pl-10 pr-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                    {loadingTargetInventory && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-purple-600"></div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Select All Products Option */}
              <div className="mb-4 flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="selectAll"
                    checked={selectAllProducts}
                    onChange={(e) => handleSelectAllProducts(e.target.checked)}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-0 focus:outline-none"
                  />
                  <label htmlFor="selectAll" className="font-semibold text-gray-700 cursor-pointer">
                    Select All Products
                  </label>
                </div>
                {selectAllProducts && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-semibold text-gray-700">Bulk Quantity:</label>
                    <input
                      type="number"
                      min="0"
                      value={bulkQuantity}
                      onChange={(e) => handleBulkQuantityChange(e.target.value)}
                      className="w-24 text-center border-2 border-blue-400 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0"
                    />
                    <span className="text-xs text-gray-600">Apply to all</span>
                  </div>
                )}
              </div>

              {/* Select Only Common Inventory Products */}
              <div className="mb-3 flex items-center justify-between bg-green-50 p-3 rounded-lg border border-green-200">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="selectCommonOnly"
                    checked={selectOnlyCommonInventory}
                    onChange={(e) => handleSelectOnlyCommonInventory(e.target.checked)}
                    className="w-5 h-5 text-green-600 rounded focus:ring-0 focus:outline-none"
                  />
                  <label htmlFor="selectCommonOnly" className="font-semibold text-gray-700 cursor-pointer">
                    Select only Common Inventory Products
                  </label>
                </div>
                {selectOnlyCommonInventory && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-semibold text-gray-700">Bulk Quantity:</label>
                    <input
                      type="number"
                      min="0"
                      value={bulkCommonQuantity}
                      onChange={(e) => handleBulkCommonQuantityChange(e.target.value)}
                      className="w-24 text-center border-2 border-green-400 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="0"
                    />
                    <span className="text-xs text-gray-600">Apply to all</span>
                  </div>
                )}
              </div>

              {/* Products List */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-800 mb-3">Select Products & Quantities</h3>
                {(() => {
                  // Filter warehouse inventory based on search query (client-side)
                  const filteredWarehouseInventory = debouncedBulkSearchQuery
                    ? inventoryData.filter(product => 
                        product.productName.toLowerCase().includes(debouncedBulkSearchQuery.toLowerCase())
                      )
                    : inventoryData;
                  
                  if (targetBranch && filteredWarehouseInventory.length === 0) {
                    return (
                      <div className="text-center py-8 text-gray-500">
                        <p className="text-lg font-semibold">No products found</p>
                        <p className="text-sm mt-2">Try adjusting your search criteria</p>
                      </div>
                    );
                  }
                  
                  return filteredWarehouseInventory.map((product) => (
                  <div key={product._id} className="border-2 border-gray-200 rounded-xl p-4 hover:border-purple-300 transition-all">
                    <h4 className="font-bold text-gray-800 mb-3">{product.productName}</h4>
                    
                    {/* If product has common inventory type, show common inventory option */}
                    {product.commonInventoryType && (
                      <div className="border-2 border-indigo-400 rounded-lg p-4 mb-4 bg-indigo-50">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id={`common-${product._id}`}
                            checked={bulkCommonInventoryData[product._id]?.isCommonInventory || false}
                            onChange={(e) => handleBulkCommonInventoryCheckboxChange(product._id, e.target.checked, product)}
                            className="w-5 h-5 text-indigo-600 rounded focus:ring-0 focus:outline-none mt-1 flex-shrink-0"
                          />
                          <div className="flex-1">
                            <label htmlFor={`common-${product._id}`} className="font-semibold text-indigo-900 cursor-pointer block mb-3">
                              📦 Common Inventory (Metric-based)
                            </label>
                            
                            {bulkCommonInventoryData[product._id]?.isCommonInventory && (
                              <div className="space-y-3">
                                {/* Quantity Input with Metric Display */}
                                <div>
                                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                    Quantity ({bulkCommonInventoryData[product._id]?.mainMetric?.metricLabel || product?.mainMetric?.metricLabel || 'N/A'}) <span className="text-red-500">*</span>
                                  </label>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.1"
                                      value={bulkCommonInventoryData[product._id]?.quantity || ''}
                                      onChange={(e) => handleBulkCommonInventoryQuantityChange(product._id, e.target.value)}
                                      className="flex-1 text-center border-2 border-indigo-400 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                      placeholder="0"
                                    />
                                    {(bulkCommonInventoryData[product._id]?.mainMetric?.metricLabel || product?.mainMetric?.metricLabel) && (
                                      <span className="text-sm font-semibold text-indigo-700 bg-indigo-100 px-2.5 py-1.5 rounded-md whitespace-nowrap border border-indigo-200">
                                        {bulkCommonInventoryData[product._id]?.mainMetric?.metricLabel || product?.mainMetric?.metricLabel}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-600 mt-1">
                                    Available: {product.options[0]?.availableQuantity || 0}
                                  </p>
                                  {targetBranch && (
                                    <p className="text-xs text-gray-600 mt-1">
                                      Target Branch: {targetBranchInventory.find(p => p._id === product._id)?.options.find(o => o._id === product.options[0]?._id)?.availableQuantity ?? 0}
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Regular Product Options - Disabled if Common Inventory is enabled */}
                    {product.options.map((option, optionIndex) => {
                      const key = `${product._id}_${option._id}`;
                      const isSelected = selectedProducts.includes(key);
                      // Find target branch inventory for this product option
                      const targetProduct = targetBranchInventory.find(p => p._id === product._id);
                      const targetOption = targetProduct?.options.find(o => o._id === option._id);
                      const targetAvailable = targetOption?.availableQuantity ?? 0;
                      
                      // For common inventory, don't show target branch info at all
                      const showTargetBranch = !product.commonInventoryType;
                      
                      return (
                        <div key={option._id} className={`flex items-center gap-4 mb-3 p-3 bg-gray-50 rounded-lg ${product.commonInventoryType ? 'opacity-40 pointer-events-none grayscale' : ''}`}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => handleProductSelection(product._id, option._id, e.target.checked)}
                            className="w-5 h-5 text-purple-600 rounded focus:ring-0 focus:outline-none"
                            disabled={product.commonInventoryType}
                          />
                          <span className={`${option.combinationLabel ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'} text-sm font-semibold px-3 py-1 rounded-full min-w-[80px] text-center`}>
                            {getOptionDisplayLabel(option)}
                          </span>
                          <div className="flex-1">
                            <div className="flex flex-col gap-1">
                              <div className={product.commonInventoryType ? 'opacity-0' : ''}>
                                <span className="text-xs text-gray-500">Warehouse: </span>
                                <span className={`text-sm font-bold ${option.availableQuantity < 10 ? 'text-red-600' : 'text-green-600'}`}>
                                  {option.availableQuantity}
                                </span>
                              </div>
                              {targetBranch && showTargetBranch && (
                                <div>
                                  <span className="text-xs text-gray-500">Target Branch: </span>
                                  <span className={`text-sm font-bold ${loadingTargetInventory ? 'text-gray-400' : targetAvailable < 10 ? 'text-orange-600' : 'text-blue-600'}`}>
                                    {loadingTargetInventory ? '...' : targetAvailable}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          {isSelected && !product.commonInventoryType && (
                            <div className="flex items-center gap-2">
                              <label className="text-sm font-semibold text-gray-700">Quantity:</label>
                              <input
                                type="number"
                                min="0"
                                max={option.availableQuantity}
                                value={distributionQuantities[key] !== undefined ? distributionQuantities[key] : ''}
                                onChange={(e) => handleDistributionQuantityChange(product._id, option._id, e.target.value)}
                                className="w-24 text-center border-2 border-purple-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                placeholder="0"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  ));
                })()}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={handleCloseBulkDistribution}
                disabled={isDistributing}
                className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitBulkDistribution}
                disabled={isDistributing || !targetBranch || (selectedProducts.length === 0 && !Object.values(bulkCommonInventoryData).some(data => data?.isCommonInventory))}
                className="px-6 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDistributing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    <span>Creating Request...</span>
                  </>
                ) : (
                  <>
                    <FaClipboardCheck />
                    <span>Send for Approval</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Branch Stock Request Modal */}
      {showStockRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <FaStore className="text-3xl" />
                  <div>
                    <h2 className="text-2xl font-bold">Request Stock from Warehouse</h2>
                    <p className="text-emerald-100 text-sm mt-1">Select products you need from the warehouse</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseStockRequest}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Source Warehouse Selection */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Source Warehouse *
                </label>
                <Select
                  options={branchOptions.filter(b => (b.isWarehouse || b.masterAccount) && b.value !== selectedBranch?.value)}
                  value={sourceWarehouse}
                  onChange={handleSourceWarehouseChange}
                  placeholder="Choose warehouse to request stock from..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>

              {/* Search Input */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Search Products
                  </label>
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={stockRequestSearch}
                      onChange={(e) => setStockRequestSearch(e.target.value)}
                      placeholder="Search by product name..."
                      className="w-full pl-10 pr-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                    {loadingWarehouseInventory && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-emerald-600"></div>
                      </div>
                    )}
                  </div>
                </div>

              {sourceWarehouse && (
                <>
                  {/* Select All Products */}
                  <div className="mb-4 flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="stockReqSelectAll"
                        checked={stockRequestSelectAll}
                        onChange={(e) => handleStockRequestSelectAll(e.target.checked)}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-0 focus:outline-none"
                      />
                      <label htmlFor="stockReqSelectAll" className="font-semibold text-gray-700 cursor-pointer">
                        Select All Products
                      </label>
                    </div>
                    {stockRequestSelectAll && (
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-semibold text-gray-700">Bulk Quantity:</label>
                        <input
                          type="number"
                          min="0"
                          value={stockRequestBulkQty}
                          onChange={(e) => handleStockRequestBulkQtyChange(e.target.value)}
                          className="w-24 text-center border-2 border-blue-400 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0"
                        />
                        <span className="text-xs text-gray-600">Apply to all</span>
                      </div>
                    )}
                  </div>

                  {/* Select Only Common Inventory */}
                  <div className="mb-3 flex items-center justify-between bg-green-50 p-3 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="stockReqSelectCommon"
                        checked={stockRequestSelectCommon}
                        onChange={(e) => handleStockRequestSelectCommon(e.target.checked)}
                        className="w-5 h-5 text-green-600 rounded focus:ring-0 focus:outline-none"
                      />
                      <label htmlFor="stockReqSelectCommon" className="font-semibold text-gray-700 cursor-pointer">
                        Select only Common Inventory Products
                      </label>
                    </div>
                    {stockRequestSelectCommon && (
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-semibold text-gray-700">Bulk Quantity:</label>
                        <input
                          type="number"
                          min="0"
                          value={stockRequestBulkCommonQty}
                          onChange={(e) => handleStockRequestBulkCommonQtyChange(e.target.value)}
                          className="w-24 text-center border-2 border-green-400 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          placeholder="0"
                        />
                        <span className="text-xs text-gray-600">Apply to all</span>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Products List */}
              <div className="space-y-4">
                {sourceWarehouse && <h3 className="text-lg font-bold text-gray-800 mb-3">Select Products & Quantities to Request</h3>}
                {(() => {
                  const filteredInventory = debouncedStockRequestSearch
                    ? warehouseInventoryData.filter(product =>
                        product.productName.toLowerCase().includes(debouncedStockRequestSearch.toLowerCase())
                      )
                    : warehouseInventoryData;

                  if (sourceWarehouse && filteredInventory.length === 0) {
                    return (
                      <div className="text-center py-8 text-gray-500">
                        <p className="text-lg font-semibold">
                          {loadingWarehouseInventory ? 'Loading warehouse inventory...' : 'No products found'}
                        </p>
                        {!loadingWarehouseInventory && <p className="text-sm mt-2">Try adjusting your search criteria</p>}
                      </div>
                    );
                  }

                  return filteredInventory.map((product) => (
                    <div key={product._id} className="border-2 border-gray-200 rounded-xl p-4 hover:border-emerald-300 transition-all">
                      <h4 className="font-bold text-gray-800 mb-3">{product.productName}</h4>

                      {/* Common Inventory Option */}
                      {product.commonInventoryType && (
                        <div className="border-2 border-indigo-400 rounded-lg p-4 mb-4 bg-indigo-50">
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              id={`stock-common-${product._id}`}
                              checked={stockRequestCommonData[product._id]?.isCommonInventory || false}
                              onChange={(e) => handleStockRequestCommonCheckbox(product._id, e.target.checked, product)}
                              className="w-5 h-5 text-indigo-600 rounded focus:ring-0 focus:outline-none mt-1 flex-shrink-0"
                            />
                            <div className="flex-1">
                              <label htmlFor={`stock-common-${product._id}`} className="font-semibold text-indigo-900 cursor-pointer block mb-3">
                                📦 Common Inventory (Metric-based)
                              </label>

                              {stockRequestCommonData[product._id]?.isCommonInventory && (
                                <div className="space-y-3">
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                      Quantity ({stockRequestCommonData[product._id]?.mainMetric?.metricLabel || product?.mainMetric?.metricLabel || 'N/A'}) <span className="text-red-500">*</span>
                                    </label>
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.1"
                                        value={stockRequestCommonData[product._id]?.quantity || ''}
                                        onChange={(e) => handleStockRequestCommonQtyChange(product._id, e.target.value)}
                                        className="flex-1 text-center border-2 border-indigo-400 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="0"
                                      />
                                      {(stockRequestCommonData[product._id]?.mainMetric?.metricLabel || product?.mainMetric?.metricLabel) && (
                                        <span className="text-sm font-semibold text-indigo-700 bg-indigo-100 px-2.5 py-1.5 rounded-md whitespace-nowrap border border-indigo-200">
                                          {stockRequestCommonData[product._id]?.mainMetric?.metricLabel || product?.mainMetric?.metricLabel}
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-600 mt-1">
                                      Warehouse Available: {product.options[0]?.availableQuantity || 0}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Regular Product Options */}
                      {product.options.map((option) => {
                        const key = `${product._id}_${option._id}`;
                        const isSelected = stockRequestProducts.includes(key);

                        return (
                          <div key={option._id} className={`flex items-center gap-4 mb-3 p-3 bg-gray-50 rounded-lg ${product.commonInventoryType ? 'opacity-40 pointer-events-none grayscale' : ''}`}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => handleStockRequestProductSelection(product._id, option._id, e.target.checked)}
                              className="w-5 h-5 text-emerald-600 rounded focus:ring-0 focus:outline-none"
                              disabled={product.commonInventoryType}
                            />
                            <span className={`${option.combinationLabel ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'} text-sm font-semibold px-3 py-1 rounded-full min-w-[80px] text-center`}>
                              {getOptionDisplayLabel(option)}
                            </span>
                            <div className="flex-1">
                              <div className="flex flex-col gap-1">
                                <div className={product.commonInventoryType ? 'opacity-0' : ''}>
                                  <span className="text-xs text-gray-500">Warehouse Available: </span>
                                  <span className={`text-sm font-bold ${option.availableQuantity < 10 ? 'text-red-600' : 'text-green-600'}`}>
                                    {option.availableQuantity}
                                  </span>
                                </div>
                              </div>
                            </div>
                            {isSelected && !product.commonInventoryType && (
                              <div className="flex items-center gap-2">
                                <label className="text-sm font-semibold text-gray-700">Quantity:</label>
                                <input
                                  type="number"
                                  min="0"
                                  max={option.availableQuantity}
                                  value={stockRequestQuantities[key] !== undefined ? stockRequestQuantities[key] : ''}
                                  onChange={(e) => handleStockRequestQuantityChange(product._id, option._id, e.target.value)}
                                  className="w-24 text-center border-2 border-emerald-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                  placeholder="0"
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={handleCloseStockRequest}
                disabled={isRequestingStock}
                className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitStockRequest}
                disabled={isRequestingStock || !sourceWarehouse || (stockRequestProducts.length === 0 && !Object.values(stockRequestCommonData).some(d => d?.isCommonInventory))}
                className="px-6 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg font-semibold hover:from-emerald-700 hover:to-teal-700 shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isRequestingStock ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    <span>Sending Request...</span>
                  </>
                ) : (
                  <>
                    <FaClipboardCheck />
                    <span>Send Stock Request</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters Modal */}
      {showFiltersModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-500 to-blue-600">
              <div className="flex items-center gap-2">
                <FaFilter className="text-white text-lg" />
                <h2 className="text-lg font-bold text-white">Filters</h2>
              </div>
              <button
                onClick={() => setShowFiltersModal(false)}
                className="p-2 rounded-lg hover:bg-white/20 text-white transition-colors"
              >
                <FaTimes className="text-lg" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Category Filter */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Category
                </label>
                <Select
                  options={categoryOptions}
                  value={pendingCategory}
                  onChange={(opt) => {
                    setPendingCategory(opt);
                    setPendingSubcategory(null); // Reset subcategory when category changes
                  }}
                  placeholder="All Categories"
                  isClearable
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>

              {/* Subcategory Filter */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Subcategory
                </label>
                <Select
                  options={modalSubcategoryOptions}
                  value={pendingSubcategory}
                  onChange={(opt) => setPendingSubcategory(opt)}
                  placeholder="All Subcategories"
                  isClearable
                  isDisabled={!pendingCategory || modalSubcategoryOptions.length === 0}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
                {!pendingCategory && (
                  <p className="text-xs text-gray-400 mt-1">
                    Select a category first to see subcategories
                  </p>
                )}
              </div>

              {/* Active Filters Summary */}
              {(pendingCategory || pendingSubcategory) && (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <h3 className="text-sm font-semibold text-blue-800 mb-2">Selected Filters</h3>
                  <div className="flex flex-wrap gap-2">
                    {pendingCategory && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full">
                        {pendingCategory.label}
                        <button
                          onClick={() => {
                            setPendingCategory(null);
                            setPendingSubcategory(null);
                          }}
                          className="hover:text-blue-900"
                        >
                          <FaTimes className="text-xs" />
                        </button>
                      </span>
                    )}
                    {pendingSubcategory && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full">
                        {pendingSubcategory.label}
                        <button
                          onClick={() => setPendingSubcategory(null)}
                          className="hover:text-blue-900"
                        >
                          <FaTimes className="text-xs" />
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => {
                  setPendingCategory(null);
                  setPendingSubcategory(null);
                  setSelectedCategory(null);
                  setSelectedSubcategory(null);
                  setShowFiltersModal(false);
                }}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 transition-colors"
              >
                Clear All
              </button>
              <button
                onClick={() => {
                  setSelectedCategory(pendingCategory);
                  setSelectedSubcategory(pendingSubcategory);
                  setShowFiltersModal(false);
                }}
                className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium hover:shadow-lg transition-all"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryManagement;
